import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { Bell, Pill, Calendar, AlertTriangle, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ user, onLogout }) => {
  const [settings, setSettings] = useState({
    appointment_reminders: true,
    medication_reminders: true,
    symptom_alerts: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings({
        appointment_reminders: response.data.appointment_reminders,
        medication_reminders: response.data.medication_reminders,
        symptom_alerts: response.data.symptom_alerts,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleToggle = async (settingName) => {
    const newValue = !settings[settingName];
    const token = localStorage.getItem('token');

    try {
      await axios.put(
        `${API}/settings`,
        { [settingName]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings({ ...settings, [settingName]: newValue });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4">
        <Navigation active="profile" />
        <div className="max-w-4xl mx-auto pt-8 text-center">
          <h2 className="text-2xl font-semibold mb-4">Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="settings-page">
      <Navigation active="profile" />

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#333' }}>Settings</h1>
          <p className="text-gray-600 mt-1">Manage notifications and reminders</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-6"
          data-testid="notification-settings"
        >
          <div className="flex items-center gap-2 mb-6">
            <Bell size={24} style={{ color: '#A2DAD3' }} />
            <h2 className="text-xl font-semibold" style={{ color: '#A2DAD3' }}>Notifications & Reminders</h2>
          </div>

          <div className="space-y-4">
            <div
              className="flex items-center justify-between p-4 rounded-xl"
              style={{ backgroundColor: '#f0f9f8', border: '2px solid #e0f0ee' }}
              data-testid="appointment-reminders-setting"
            >
              <div className="flex items-center gap-3">
                <Calendar size={20} style={{ color: '#A7C7F9' }} />
                <div>
                  <p className="font-medium">Appointment Reminders</p>
                  <p className="text-sm text-gray-600">Get notified about upcoming appointments</p>
                </div>
              </div>
              <button
                data-testid="toggle-appointment-reminders"
                onClick={() => handleToggle('appointment_reminders')}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 ease-in-out ${
                  settings.appointment_reminders ? 'bg-[#A2DAD3]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                    settings.appointment_reminders ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div
              className="flex items-center justify-between p-4 rounded-xl"
              style={{ backgroundColor: '#fff5f5', border: '2px solid #ffe5e5' }}
              data-testid="medication-reminders-setting"
            >
              <div className="flex items-center gap-3">
                <Pill size={20} style={{ color: '#F9A7A7' }} />
                <div>
                  <p className="font-medium">Medication Reminders</p>
                  <p className="text-sm text-gray-600">Receive alerts for medication times</p>
                </div>
              </div>
              <button
                data-testid="toggle-medication-reminders"
                onClick={() => handleToggle('medication_reminders')}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 ease-in-out ${
                  settings.medication_reminders ? 'bg-[#F9A7A7]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                    settings.medication_reminders ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div
              className="flex items-center justify-between p-4 rounded-xl"
              style={{ backgroundColor: '#fff9f0', border: '2px solid #ffe5cc' }}
              data-testid="symptom-alerts-setting"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} style={{ color: '#F9D6A7' }} />
                <div>
                  <p className="font-medium">Symptom Alerts</p>
                  <p className="text-sm text-gray-600">Get alerts for repeated symptoms</p>
                </div>
              </div>
              <button
                data-testid="toggle-symptom-alerts"
                onClick={() => handleToggle('symptom_alerts')}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 ease-in-out ${
                  settings.symptom_alerts ? 'bg-[#F9D6A7]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                    settings.symptom_alerts ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
          style={{ backgroundColor: '#f0f9f8', border: '2px solid #A2DAD3' }}
        >
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> These settings control in-app notifications and alerts. Push notifications may require additional browser permissions.
          </p>
        </motion.div>

        {/* Legal Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card"
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#A2DAD3' }}>Legal</h2>
          <div className="space-y-3">
            <a 
              href="/terms" 
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition"
            >
              <span className="text-gray-700">Terms & Conditions</span>
              <span style={{ color: '#A2DAD3' }}>→</span>
            </a>
            <a 
              href="/privacy" 
              className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition"
            >
              <span className="text-gray-700">Privacy Policy</span>
              <span style={{ color: '#A2DAD3' }}>→</span>
            </a>
          </div>
        </motion.div>

        <div className="app-footer">
          Made with <Heart size={16} style={{ color: '#F9A7A7', display: 'inline' }} fill="#F9A7A7" /> for caregivers
        </div>
      </div>
    </div>
  );
};

export default Settings;
